import { whereText, fullLabel } from "./WhereItIs";
import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { normalizeScan } from "@/lib/scan";
import { MapPin, ScanLine, CheckCircle2, XCircle, Search, Loader2, Camera, CameraOff, History, Flashlight, FlashlightOff, Focus } from "lucide-react";
import { ScanTextInput } from "@/components/ScanTextInput";

const CAMERA_ID = "putaway-camera-reader";

const timeShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

// Inline single-part putaway. Reuses POST /putaway (part_scan + location_id).
// Supports phone camera + scan gun (keyboard-wedge) + manual typing.
export function PutAwayDialog({ part, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [scan, setScan] = useState("");
  const [picked, setPicked] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [focusSupported, setFocusSupported] = useState(false);
  const [recent, setRecent] = useState([]);
  const scanRef = useRef(null);
  const qrRef = useRef(null);

  const partScan = part.tag_code || part.short_code || part.qr_token;

  const loadRecent = () => api.get("/putaway/recent?limit=6").then(({ data }) => setRecent(data || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setScan(""); setPicked(null); setFilter(""); setResult(null); setCamErr("");
    api.get("/locations").then(({ data }) => setLocations((data || []).filter((l) => l.type !== "site"))).catch(() => {});
    loadRecent();
    setTimeout(() => scanRef.current?.focus(), 80);
  }, [open]);

  // Camera lifecycle
  useEffect(() => {
    if (!camOn) return;
    let active = true;
    const start = async () => {
      try {
        const qr = new Html5Qrcode(CAMERA_ID, {
          verbose: false,
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_128],
        });
        qrRef.current = qr;
        await qr.start(
          // Camera SELECTOR must have EXACTLY ONE key (html5-qrcode contract) — just pick the back camera.
          { facingMode: "environment" },
          {
            fps: 15,
            // Responsive scan box (~72% of the smaller viewfinder edge) instead of a fixed 240px target.
            qrbox: (vw, vh) => { const e = Math.floor(Math.min(vw, vh) * 0.72); return { width: e, height: e }; },
            aspectRatio: 1.0,
            // High resolution belongs HERE (videoConstraints), not in the selector arg, so small / screen-shown QRs have enough pixels.
            videoConstraints: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
            // Use the OS-native BarcodeDetector where supported (Android / desktop Chrome);
            // iOS Safari has none and transparently falls back to the bundled ZXing decoder.
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          },
          (decoded) => {
            if (!active) return;
            const val = normalizeScan(decoded);
            scanAccept(val);
            setScan(val);
            stopCamera();
            doPutaway(val, "camera");
          },
          () => {});
        if (!active) {
          // Dialog closed while the camera was still starting — release it now so it isn't left locked.
          await releaseQr(qr);
          qrRef.current = null;
          return;
        }
        // Detect MANUAL torch / focus controls (best-effort). User-triggered only — nothing automatic.
        try {
          const caps = (qr.getRunningTrackCapabilities && qr.getRunningTrackCapabilities()) || {};
          setTorchSupported(!!caps.torch);
          setFocusSupported(Array.isArray(caps.focusMode) ? caps.focusMode.length > 0 : !!caps.focusMode);
        } catch { /* capabilities not exposed by this device/browser */ }
      } catch (e) {
        // Surface the REAL underlying error (NotReadableError = camera busy, NotAllowedError = permission, etc.).
        console.error("Camera start failed:", e);
        const detail = e && e.name ? `${e.name}${e.message ? ": " + e.message : ""}` : ((e && e.message) || String(e));
        setCamErr(`Camera error — ${detail}. Allow camera access, close other apps using the camera, or use the scan gun / pick a bin below.`);
        await stopCameraQuiet();
        setCamOn(false);
      }
    };
    start();
    return () => { active = false; stopCameraQuiet(); };
    // eslint-disable-next-line
  }, [camOn]);

  // Release an html5-qrcode instance on EVERY exit path. State-guarded stop avoids the
  // throw that html5-qrcode raises when stop() is called outside SCANNING/PAUSED; the
  // defensive track-stop guarantees the camera is freed even if the library misbehaves.
  const releaseQr = async (qr) => {
    if (qr) {
      try {
        const st = qr.getState && qr.getState();
        if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
          await qr.stop();
        }
      } catch (e) { console.warn("html5-qrcode stop:", (e && e.message) || e); }
      try { await qr.clear(); } catch { /* ignore */ }
    }
    // Defensive: kill any MediaStream tracks still attached to the viewfinder <video>,
    // so a missed/failed stop can never leave the camera locked for the next open.
    try {
      const el = document.getElementById(CAMERA_ID);
      const video = el && el.querySelector("video");
      const stream = video && video.srcObject;
      if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    } catch { /* ignore */ }
  };

  const stopCameraQuiet = async () => {
    const qr = qrRef.current;
    qrRef.current = null;
    await releaseQr(qr);
    setTorchOn(false); setTorchSupported(false); setFocusSupported(false);
  };
  const stopCamera = async () => { await stopCameraQuiet(); setCamOn(false); };

  // Manual torch toggle (user taps it).
  const toggleTorch = async () => {
    const qr = qrRef.current;
    if (!qr) return;
    const next = !torchOn;
    try { await qr.applyVideoConstraints({ advanced: [{ torch: next }] }); setTorchOn(next); }
    catch { toast.error("Torch isn't available on this device/browser."); }
  };

  // Manual refocus (user taps the video or the button). True point-focus is rarely
  // supported on the web; this nudges the camera to re-run continuous autofocus.
  const tapFocus = async () => {
    const qr = qrRef.current;
    if (!qr || !focusSupported) return;
    try { await qr.applyVideoConstraints({ advanced: [{ focusMode: "continuous" }] }); toast.success("Refocusing…"); }
    catch { /* ignore */ }
  };

  const closeAll = async (v) => {
    if (!v) await stopCameraQuiet();
    setOpen(v);
  };

  const doPutaway = async (binValue, input = "unknown") => {
    if (!binValue) { toast.error("Scan or pick a destination bin first."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/putaway", { part_scan: partScan, location_id: binValue,
                                                    provenance: prov("putaway_dialog", { input, code: binValue }) });
      if (data.ok) {
        scanAccept(`PUT AWAY IN ${data.location?.short_code || ""}`.trim());
        setResult({ ok: true, message: `Put away ${whereText(data.location) || data.location?.short_code || ""}`.trim(), location: data.location });
        toast.success("Part location updated");
        onDone && onDone();
        loadRecent();
      } else {
        scanRefuse(data.message || "COULD NOT PUT THIS PART AWAY");
        setResult({ ok: false, message: data.message || "Could not put this part away." });
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "COULD NOT PUT THIS PART AWAY");
      setResult({ ok: false, message: msg });
    } finally { setBusy(false); }
  };

  const submitScan = (e) => {
    e.preventDefault();
    const code = normalizeScan(scan);
    if (!code) { toast.error("Scan a bin or pick one below."); return; }
    setPicked(null);
    doPutaway(code);
  };

  const filtered = locations.filter((l) => {
    const qq = filter.trim().toLowerCase();
    if (!qq) return true;
    return `${l.short_code} ${fullLabel(l)} ${l.name || ""}`.toLowerCase().includes(qq);
  }).slice(0, 60);

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={closeAll}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md max-h-kb overflow-y-auto" data-testid="putaway-inline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><MapPin className="w-4 h-4 text-[#B01E28]" />Put away / move</DialogTitle>
            <DialogDescription>
              Set the destination bin for <span className="font-mono font-semibold">{partScan}</span> — {part.name}.
            </DialogDescription>
          </DialogHeader>

          {/* PRIMARY: scan gun / manual type */}
          <form onSubmit={submitScan} className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Scan destination bin (QR / barcode)</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <ScanLine className="w-4 h-4 text-slate-400 absolute left-2.5 top-[1.15rem] -translate-y-1/2 z-10" />
                <ScanTextInput inputRef={scanRef} testid="putaway-inline-scan" value={scan}
                  onChange={setScan} placeholder="Scan gun or type bin code…"
                  inputClassName="pl-8" autoComplete="off" />
              </div>
              <Button type="submit" data-testid="putaway-inline-scan-submit" disabled={busy}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white shrink-0">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Put away"}
              </Button>
            </div>
          </form>

          {/* CAMERA */}
          <div className="space-y-2">
            {!camOn ? (
              <Button type="button" variant="outline" data-testid="putaway-camera-start"
                onClick={() => { setCamErr(""); setCamOn(true); }}
                className="w-full rounded-lg border-slate-300 h-9">
                <Camera className="w-4 h-4 mr-1.5" />Scan with camera
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <div id={CAMERA_ID} data-testid="putaway-camera-view"
                    onClick={focusSupported ? tapFocus : undefined}
                    className={`w-full overflow-hidden rounded-lg border border-slate-300 bg-black ${focusSupported ? "cursor-pointer" : ""}`} />
                  {focusSupported && (
                    <span className="absolute bottom-1.5 left-1.5 text-[10px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded pointer-events-none">Tap video to refocus</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {torchSupported && (
                    <Button type="button" variant="outline" data-testid="putaway-camera-torch" onClick={toggleTorch}
                      className={`flex-1 rounded-lg h-9 ${torchOn ? "border-amber-400 text-amber-700 bg-amber-50" : "border-slate-300"}`}>
                      {torchOn ? <FlashlightOff className="w-4 h-4 mr-1.5" /> : <Flashlight className="w-4 h-4 mr-1.5" />}{torchOn ? "Torch off" : "Torch on"}
                    </Button>
                  )}
                  {focusSupported && (
                    <Button type="button" variant="outline" data-testid="putaway-camera-focus" onClick={tapFocus}
                      className="flex-1 rounded-lg h-9 border-slate-300"><Focus className="w-4 h-4 mr-1.5" />Refocus</Button>
                  )}
                  <Button type="button" variant="outline" data-testid="putaway-camera-stop" onClick={stopCamera}
                    className="flex-1 rounded-lg border-slate-300 h-9">
                    <CameraOff className="w-4 h-4 mr-1.5" />Stop
                  </Button>
                </div>
                {!torchSupported && !focusSupported && (
                  <p className="text-[10px] text-slate-400">Torch / focus controls aren't exposed by this device/browser.</p>
                )}
              </div>
            )}
            {camErr && <p data-testid="putaway-camera-error" className="text-xs text-[#B01E28]">{camErr}</p>}
          </div>

          {/* FALLBACK: searchable bin list */}
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Or pick a bin (for damaged / unreachable labels)</Label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-[1.15rem] -translate-y-1/2 z-10" />
              <ScanTextInput testid="putaway-inline-filter" value={filter} onChange={setFilter}
                placeholder="Search bins by code or location…" inputClassName="pl-8" autoComplete="off" />
            </div>
            <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <div className="p-3 text-xs text-slate-400 text-center">No matching bins.</div>
              ) : filtered.map((l) => (
                <button key={l.id} type="button" data-testid={`putaway-inline-bin-${l.short_code}`}
                  onClick={() => { setPicked({ id: l.id, label: `${l.short_code} · ${fullLabel(l) || l.name || ""}` }); setScan(""); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${picked?.id === l.id ? "bg-amber-50" : ""}`}>
                  <span className="font-mono font-medium text-slate-800">{l.short_code}</span>
                  <span className="text-slate-500"> · {fullLabel(l) || l.name}</span>
                </button>
              ))}
            </div>
            {picked && (
              <Button data-testid="putaway-inline-pick-submit" disabled={busy} onClick={() => doPutaway(picked.id, "tap")}
                className="w-full rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Put away in {picked.label}</>}
              </Button>
            )}
          </div>

          {result && (
            <div data-testid="putaway-inline-result"
              className={`flex items-center gap-2 rounded-lg p-3 text-sm border-2 ${result.ok ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-red-500 bg-red-50 text-red-700"}`}>
              {result.ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {result.message}
            </div>
          )}

          {/* RECENTLY PUT AWAY (reads ledger) */}
          {recent.length > 0 && (
            <div className="space-y-1.5" data-testid="putaway-recent-strip">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><History className="w-3.5 h-3.5" />Recently put away</Label>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {recent.map((r) => (
                  <div key={r.id} data-testid={`putaway-recent-${r.tx_number}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="font-mono font-medium text-slate-800 truncate">{r.part?.code || "—"}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-mono text-slate-600 truncate">{r.to_location?.short_code || "—"}</span>
                    <span className="ml-auto font-mono text-[10px] text-slate-400 shrink-0">{timeShort(r.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
